// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
/**
 * Dado de leitura das abas "Medições" e "Demonstrativos" do Ciclo mensal da SME.
 *
 * A API não existe: a tabela `medicoes` está migrada, mas não há módulo que a
 * sirva à SME em `apps/api/src/modulos/`. O mock entra sob a exceção registrada
 * em `CLAUDE.md` para estas duas telas — só leitura, dado claramente fictício,
 * e some antes de qualquer promoção `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist de `docs/ALVOS.md`).
 *
 * NENHUMA COMPETÊNCIA LITERAL. O arquivo guarda a distância em meses até hoje,
 * nunca um ano-mês escrito à mão: as duas telas filtram por competência, e mock datado de um mês
 * morto envelhece sozinho — e derruba teste numa PR que não encostou aqui. Há
 * teste que falha se alguém escrever uma competência fixa neste arquivo.
 */

import { useQuery } from '@tanstack/react-query';

import { MOCK_DRES } from '../dados-simulados';
import type { DreDaMedicao, MedicaoSme, StatusMedicaoSme, TipoDocumentoLiquidacao } from './tipos';

/** Competência `yyyy-mm` deslocada `mesesAtras` meses a partir do mês corrente. */
export function competenciaAtras(mesesAtras: number): string {
  const hoje = new Date();
  const data = new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

/** Uma DRE da lista simulada, já no formato que a tela consome. */
function dre(indice: number): DreDaMedicao {
  const encontrada = MOCK_DRES[indice];
  if (!encontrada?.codigo || !encontrada.nome) {
    throw new Error(`DRE simulada ${indice} não existe ou está incompleta em MOCK_DRES.`);
  }
  return { id: encontrada.id, codigo: encontrada.codigo, nome: encontrada.nome };
}

interface Semente {
  id: string;
  mesesAtras: number;
  status: StatusMedicaoSme;
  valorTotal: string;
  termoSei: string | null;
  nome: string;
  cpfCnpj: string;
  tipo: 'PF' | 'PJ';
  dre: number;
  recusado?: boolean;
  pendentes?: TipoDocumentoLiquidacao[];
  alerta?: MedicaoSme['alertaFiscal'];
}

/**
 * Catorze medições, cobrindo os dez status, os dois tipos de pessoa e três
 * competências (mês passado, retrasado e o anterior a ele). Dois demonstrativos
 * recusados pelo credenciado, para o selo da aba ter o que contar.
 */
const SEMENTES: readonly Semente[] = [
  {
    id: 'med-sme-01',
    mesesAtras: 1,
    status: 'CALCULADA',
    valorTotal: '48210.40',
    termoSei: '6016.0000/0000001-1',
    nome: 'Rota Colibri Transportes Ltda.',
    cpfCnpj: '10.200.300/0001-40',
    tipo: 'PJ',
    dre: 0,
    pendentes: ['NOTA_FISCAL', 'CND_FEDERAL'],
    alerta: {
      nivel: 'ATENCAO',
      mensagem: 'A certidão federal vence antes da data prevista para a liquidação.',
    },
  },
  {
    id: 'med-sme-02',
    mesesAtras: 1,
    status: 'PROCESSADA',
    valorTotal: '12890.00',
    termoSei: '6016.0000/0000002-2',
    nome: 'Aurélio Fictício Ramos',
    cpfCnpj: '100.200.300-01',
    tipo: 'PF',
    dre: 1,
  },
  {
    id: 'med-sme-03',
    mesesAtras: 1,
    status: 'APROVADA',
    valorTotal: '156700.75',
    termoSei: '6016.0000/0000003-3',
    nome: 'Cooperativa Estrela do Amanhã',
    cpfCnpj: '20.300.400/0001-50',
    tipo: 'PJ',
    dre: 2,
    recusado: true,
    pendentes: ['CRF_FGTS'],
  },
  {
    id: 'med-sme-04',
    mesesAtras: 1,
    status: 'RASCUNHO',
    valorTotal: '9340.10',
    termoSei: null,
    nome: 'Beatriz Simulada Nunes',
    cpfCnpj: '200.300.400-02',
    tipo: 'PF',
    dre: 3,
  },
  {
    id: 'med-sme-05',
    mesesAtras: 1,
    status: 'EMPENHADA',
    valorTotal: '73420.00',
    termoSei: '6016.0000/0000005-5',
    nome: 'Viação Ipê de Papel Eireli',
    cpfCnpj: '30.400.500/0001-60',
    tipo: 'PJ',
    dre: 4,
  },
  {
    id: 'med-sme-06',
    mesesAtras: 1,
    status: 'REJEITADA',
    valorTotal: '15980.30',
    termoSei: '6016.0000/0000006-6',
    nome: 'Caio Exemplar Duarte',
    cpfCnpj: '300.400.500-03',
    tipo: 'PF',
    dre: 5,
  },
  {
    id: 'med-sme-07',
    mesesAtras: 2,
    status: 'LIQUIDADA',
    valorTotal: '52110.90',
    termoSei: '6016.0000/0000007-7',
    nome: 'Rota Colibri Transportes Ltda.',
    cpfCnpj: '10.200.300/0001-40',
    tipo: 'PJ',
    dre: 0,
  },
  {
    id: 'med-sme-08',
    mesesAtras: 2,
    status: 'PAGA',
    valorTotal: '13420.00',
    termoSei: '6016.0000/0000008-8',
    nome: 'Aurélio Fictício Ramos',
    cpfCnpj: '100.200.300-01',
    tipo: 'PF',
    dre: 1,
  },
  {
    id: 'med-sme-09',
    mesesAtras: 2,
    status: 'EXPORTADA_SOF',
    valorTotal: '149300.00',
    termoSei: '6016.0000/0000009-9',
    nome: 'Cooperativa Estrela do Amanhã',
    cpfCnpj: '20.300.400/0001-50',
    tipo: 'PJ',
    dre: 2,
    pendentes: ['NOTA_FISCAL'],
    alerta: {
      nivel: 'CRITICO',
      mensagem:
        'Competência com mais de 60 dias sem liquidação: risco de restos a pagar no encerramento do exercício.',
    },
  },
  {
    id: 'med-sme-10',
    mesesAtras: 2,
    status: 'CALCULADA',
    valorTotal: '11875.60',
    termoSei: '6016.0000/0000010-0',
    nome: 'Daniela Hipotética Lopes',
    cpfCnpj: '400.500.600-04',
    tipo: 'PF',
    dre: 6,
    recusado: true,
    pendentes: ['RECIBO_RPA'],
  },
  {
    id: 'med-sme-11',
    mesesAtras: 2,
    status: 'CANCELADA',
    valorTotal: '0.00',
    termoSei: null,
    nome: 'Transportes Nuvem Mansa Ltda.',
    cpfCnpj: '40.500.600/0001-70',
    tipo: 'PJ',
    dre: 7,
  },
  {
    id: 'med-sme-12',
    mesesAtras: 3,
    status: 'PAGA',
    valorTotal: '70980.45',
    termoSei: '6016.0000/0000012-2',
    nome: 'Viação Ipê de Papel Eireli',
    cpfCnpj: '30.400.500/0001-60',
    tipo: 'PJ',
    dre: 4,
  },
  {
    id: 'med-sme-13',
    mesesAtras: 3,
    status: 'PAGA',
    valorTotal: '15210.00',
    termoSei: '6016.0000/0000013-3',
    nome: 'Caio Exemplar Duarte',
    cpfCnpj: '300.400.500-03',
    tipo: 'PF',
    dre: 5,
  },
  {
    id: 'med-sme-14',
    mesesAtras: 3,
    status: 'APROVADA',
    valorTotal: '38640.20',
    termoSei: '6016.0000/0000014-4',
    nome: 'Transportes Nuvem Mansa Ltda.',
    cpfCnpj: '40.500.600/0001-70',
    tipo: 'PJ',
    dre: 7,
  },
];

function medicoesSimuladas(): MedicaoSme[] {
  return SEMENTES.map((s) => ({
    id: s.id,
    mesReferencia: competenciaAtras(s.mesesAtras),
    status: s.status,
    valorTotal: s.valorTotal,
    termoSei: s.termoSei,
    credenciado: { id: `cred-${s.cpfCnpj}`, nome: s.nome, cpfCnpj: s.cpfCnpj, tipo: s.tipo },
    dre: dre(s.dre),
    recusadoPeloCredenciado: s.recusado ?? false,
    documentosPendentes: s.pendentes ?? [],
    alertaFiscal: s.alerta ?? null,
  }));
}

/**
 * Medições da SME, de todas as DREs.
 *
 * Mantém `useQuery` — e não um arranjo importado direto — porque as telas
 * precisam dos quatro estados de verdade (CLAUDE.md, regras de interface,
 * item 3), e porque a troca para o endpoint real fica sendo só o corpo de
 * `queryFn`. Os filtros ficam na tela, como no protótipo, que resolve a
 * competência e o tipo de pessoa no cliente.
 */
export function useMedicoesSme() {
  return useQuery({
    queryKey: ['financeiro', 'ciclo-mensal-sme', 'medicoes'],
    queryFn: async () => medicoesSimuladas(),
    staleTime: 30_000,
  });
}
